import React, { useEffect, useState, useCallback } from "react";
import { getPrescriptionList, getPrescriptionByDate } from "../services/apiServices";
import {
	Box,
	Paper,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Divider,
	IconButton,
	CircularProgress,
	Alert,
	Skeleton,
	Snackbar,
	Tooltip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
	ExpandMore,
	CalendarMonth,
	RefreshOutlined,
	DescriptionOutlined,
} from "@mui/icons-material";
import { parse, format } from "date-fns";
import Api from "../Api/Api.json";
import config from "../config/config";
import PhotoPrescriptionPreview from "./PhotoPrescriptionPreview";

const PreviousPrescription = ({ patient_id }) => {
	const [records, setRecords] = useState([]);
	const [expandedPanel, setExpandedPanel] = useState(false);
	const [loading, setLocalLoading] = useState(true);
	const [error, setError] = useState(null);
	const [patientId, setPatientId] = useState(patient_id || 0);

	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "info",
	});

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// Show all records without pagination
	const currentRecords = records;

	// Fetch prescription records
	const fetchRecords = useCallback(async () => {
		setLocalLoading(true);
		setError(null);

		try {
			const result = await getPrescriptionList(patientId);
			console.log(result);
			if (result && Array.isArray(result)) {
				const sortedRecords = result.sort((a, b) => {
					const dateA = new Date(a.creation_date);
					const dateB = new Date(b.creation_date);
					return dateB - dateA; // Sort in descending order
				});

				setRecords(sortedRecords);
				if (sortedRecords.length === 0) {
					setNotification({
						open: true,
						message: "No prescription records found",
						severity: "info",
					});
				}
			} else {
				throw new Error("Failed to fetch records");
			}
		} catch (error) {
			console.error("Error fetching records:", error);
			setError(error.message || "Failed to fetch prescription records");
		} finally {
			setLocalLoading(false);
		}
	}, [patientId]);

	// Handle record expansion and fetch details
	const handlePanelChange = useCallback(
		async (panel, record) => {
			setExpandedPanel(expandedPanel === panel ? false : panel);

			if (expandedPanel !== panel && !record.prescription) {
				try {
					// Fetch record details
					const dataResponse = await getPrescriptionByDate(patientId, record.creation_date);
					console.log("Prescription details:", dataResponse);

					if (dataResponse && Array.isArray(dataResponse) && dataResponse.length > 0) {
						// Parse the prescription JSON string
						const prescriptionData = dataResponse[0];
						let parsedPrescription = prescriptionData;
						
						if (prescriptionData.prescription && typeof prescriptionData.prescription === 'string') {
							try {
								const parsed = JSON.parse(prescriptionData.prescription);
								// Map the parsed fields to the expected format
								parsedPrescription = {
									...prescriptionData,
									// Map complaints to cc_list as JSON string
									cc_list: parsed.complaints ? JSON.stringify({ complaint: parsed.complaints }) : "{}",
									// Map on_exam and diagnosis to advices_list as JSON string
									advices_list: JSON.stringify({
										on_exam: parsed.on_exam || "",
										diagnosis: parsed.diagnosis || ""
									}),
									// Add advices as a separate field
									advices: parsed.advices || "",
									// Map medicine array to medicine_list as JSON string
									medicine_list: parsed.medicine ? 
										JSON.stringify(parsed.medicine.reduce((acc, med, index) => {
											const medicineStr = `${med.name} ${med.strength || ""} (${med.type || ""}) - ${med.dosage || ""} ${med.timing || ""} for ${med.continueTill || ""}`;
											const indicationStr = med.indication ? ` - Indication: ${med.indication}` : "";
											acc[`medicine_${index}`] = medicineStr + indicationStr;
											return acc;
										}, {})) : "{}",
									// Keep original fields for backward compatibility
									...parsed
								};
							} catch (error) {
								console.error("Error parsing prescription JSON:", error);
								parsedPrescription = prescriptionData;
							}
						}
						
						// Also parse medicine_usage if it exists and convert to medicine_usage_list
						if (prescriptionData.medicine_usage && typeof prescriptionData.medicine_usage === 'string') {
							try {
								const parsedUsage = JSON.parse(prescriptionData.medicine_usage);
								parsedPrescription.medicine_usage_list = JSON.stringify(parsedUsage);
							} catch (error) {
								console.error("Error parsing medicine_usage JSON:", error);
								parsedPrescription.medicine_usage_list = "[]";
							}
						} else {
							parsedPrescription.medicine_usage_list = "[]";
						}
						
						console.log("Parsed prescription data:", parsedPrescription);
						setRecords((prev) =>
							prev.map((item) =>
								item.creation_date === record.creation_date
									? {
											...item,
											prescription: parsedPrescription,
									  }
									: item
							)
						);
					} else {
						throw new Error("Failed to fetch record details");
					}
				} catch (error) {
					console.error("Error fetching record details:", error);
					setNotification({
						open: true,
						message: "Failed to fetch prescription details",
						severity: "error",
					});
				}
			}
		},
		[expandedPanel, patientId]
	);



	// Format date and time
	const formatDateTime = useCallback((dateTime) => {
		try {
			const parsedDate = parse(
				dateTime,
				"yyyy-MM-dd HH:mm:ss",
				new Date()
			);
			return format(parsedDate, "MMMM dd, yyyy, hh:mm a");
		} catch (error) {
			console.error("Date parsing error:", error);
			return dateTime;
		}
	}, []);

	// Close notification
	const handleCloseNotification = useCallback(() => {
		setNotification((prev) => ({ ...prev, open: false }));
	}, []);

	// Refresh records
	const handleRefresh = useCallback(() => {
		fetchRecords();
		setNotification({
			open: true,
			message: "Refreshing prescription records...",
			severity: "info",
		});
	}, [fetchRecords]);

	// Update patientId when prop changes and fetch records
	useEffect(() => {
		const newPatientId = patient_id || 0;
		setPatientId(newPatientId);
		
		// Clear existing records when patient changes
		setRecords([]);
		setExpandedPanel(false);
		
		// Fetch records for the new patient
		if (newPatientId) {
			fetchRecords();
		}
	}, [patient_id, fetchRecords]);

	return (
		<Box
			className="max-w-3xl mx-auto px-4 py-6"
			style={{ minHeight: `calc(100vh - ${config.topbarHeight}px)` }}
		>
			<Box className="flex justify-between items-center mb-6">
				<Typography
					variant="h4"
					component="h1"
					className="font-bold text-blue-800"
				>
					Previous Prescriptions
				</Typography>
				<Tooltip title="Refresh records">
					<IconButton onClick={handleRefresh} color="primary">
						<RefreshOutlined />
					</IconButton>
				</Tooltip>
			</Box>

			{loading ? (
				// Loading state
				Array.from(new Array(3)).map((_, index) => (
					<Paper key={index} className="mb-4 overflow-hidden">
						<Box className="p-4">
							<Skeleton variant="rectangular" height={60} />
						</Box>
					</Paper>
				))
			) : error ? (
				// Error state
				<Alert
					severity="error"
					action={
						<LoadingButton onClick={fetchRecords}>
							Retry
						</LoadingButton>
					}
					className="mb-4"
				>
					{error}
				</Alert>
			) : records.length === 0 ? (
				// Empty state
				<Paper className="p-6 text-center">
					<Typography variant="h6" className="mb-2 text-gray-600">
						No prescription records found
					</Typography>
					<Typography variant="body2" className="mb-4 text-gray-500">
						Any prescriptions you upload will appear here
					</Typography>
					<LoadingButton
						variant="outlined"
						onClick={handleRefresh}
						startIcon={<RefreshOutlined />}
					>
						Refresh
					</LoadingButton>
				</Paper>
			) : (
				// Records list
				<>
					{currentRecords.map((record, index) => (
								<Accordion
									key={record.creation_date}
									expanded={
										expandedPanel === record.creation_date
									}
									onChange={() =>
										handlePanelChange(
											record.creation_date,
											record
										)
									}
									className="mb-4 shadow-md rounded-lg overflow-hidden border border-gray-100"
								>
									<AccordionSummary
										expandIcon={
											<IconButton
												size="small"
												className="bg-blue-50"
											>
												<ExpandMore />
											</IconButton>
										}
										className="hover:bg-blue-50 transition-colors"
									>
										<Box className="flex items-center w-full gap-3">
											<CalendarMonth className="text-blue-600" />
											<Typography
												variant="subtitle1"
												className="font-medium"
											>
												{formatDateTime(
													record.creation_date
												)}
											</Typography>
										</Box>
									</AccordionSummary>

									<Divider />

									<AccordionDetails className="p-4">
										{!record.prescription ? (
											<Box className="flex justify-center p-6">
												<CircularProgress
													size={40}
												/>
											</Box>
										) : (
											<PhotoPrescriptionPreview
												prescription={
													record.prescription
												}
											/>
										)}
									</AccordionDetails>
								</Accordion>
						  ))}


				</>
			)}

			{/* Notification */}
			<Snackbar
				open={notification.open}
				autoHideDuration={5000}
				onClose={handleCloseNotification}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleCloseNotification}
					severity={notification.severity}
					variant="filled"
				>
					{notification.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default PreviousPrescription;
